import React, { useState, useEffect } from "react";
import {
  Box,
  Input,
  Button,
  FormControl,
  FormLabel,
  Select,
  VStack,
  HStack,
  Text,
  Spinner,
  IconButton,
  Grid,
  GridItem,
  useToast,
} from "@chakra-ui/react";
import { Icon } from "@iconify/react";
import axios from "axios";
import { getAxiosConfig } from "../../../utils/helpers";
import PopupWrapper from "../../../components/PopupWrapper";
import { FiPlus, FiTrash2, FiUpload } from "react-icons/fi";

const AddMaterialButton = ({ onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = (isSubmit) => {
    setIsOpen(false);
    if (typeof onSuccess === "function" && !!isSubmit) {
      onSuccess();
    }
  };

  return (
    <div>
      <Button
        onClick={() => setIsOpen(true)}
        size={"sm"}
        colorScheme="teal"
        display={"flex"}
        alignItems={"center"}
        gap={2}
      >
        Add Material
        <Icon width={16} icon="fluent:add-20-regular" />
      </Button>

      {isOpen && (
        <PopupWrapper
          onClose={() => setIsOpen(false)}
          heading="Add Material"
          width="max-w-4xl"
          isOverflow={true}
        >
          <MaterialFormComponent onClose={handleClose} />
        </PopupWrapper>
      )}
    </div>
  );
};

const MaterialFormComponent = ({ onClose }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Options from API
  const [areasOptions, setAreasOptions] = useState([]);
  const [materialTypeOptions, setMaterialTypeOptions] = useState([]);

  // Form Fields
  const [materialName, setMaterialName] = useState("");
  const [brand, setBrand] = useState("");
  const [productCode, setProductCode] = useState("");
  const [mrpPrice, setMrpPrice] = useState("");
  //   const [color, setColor] = useState("");
  const [material, setMaterial] = useState("");
  const [size, setSize] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedMaterialType, setSelectedMaterialType] = useState("");
  const [customMaterialType, setCustomMaterialType] = useState("");
  const [isCustomType, setIsCustomType] = useState(false);
  const [otherDetails, setOtherDetails] = useState([{ key: "", value: "" }]);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // Fetch areas and materialType options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoadingOptions(true);
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACK_URL}/api/material/get-all-mainfilter?filter={"areas":[],"brand":[]}`,
          getAxiosConfig(),
        );

        // Parse areas
        if (data?.data?.areas?.[0]?.areas) {
          setAreasOptions(data.data.areas[0].areas);
        }

        // Parse material types
        if (data?.data?.collections) {
          const types = data.data.collections
            .map((c) => c.type)
            .filter(Boolean);
          setMaterialTypeOptions(types);
        }
      } catch (error) {
        console.error("Error fetching form options:", error);
        toast({
          title: "Error fetching options",
          description: "Failed to load areas and material types.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [toast]);

  // Handle otherDetails Change
  const handleDetailChange = (index, field, val) => {
    const updated = [...otherDetails];
    updated[index][field] = val;
    setOtherDetails(updated);
  };

  const addDetailRow = () => {
    setOtherDetails([...otherDetails, { key: "", value: "" }]);
  };

  const removeDetailRow = (index) => {
    if (otherDetails.length > 1) {
      setOtherDetails(otherDetails.filter((_, i) => i !== index));
    } else {
      setOtherDetails([{ key: "", value: "" }]);
    }
  };

  // Handle Image Upload
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    // Only allow images
    const invalidFiles = files.filter((f) => !f.type.startsWith("image/"));
    if (invalidFiles.length > 0) {
      toast({
        title: "Invalid file type",
        description: "Only images are allowed.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setImages((prev) => [...prev, ...files]);

    // Create previews
    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews((prev) => [...prev, ...previews]);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!materialName.trim()) {
      toast({ title: "Material Name is required", status: "warning" });
      return;
    }

    const matType = isCustomType
      ? customMaterialType.trim()
      : selectedMaterialType;
    if (!matType) {
      toast({ title: "Material Type is required", status: "warning" });
      return;
    }

    if (!selectedArea) {
      toast({ title: "Area is required", status: "warning" });
      return;
    }

    if (images.length === 0) {
      toast({ title: "At least one image is required", status: "warning" });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("materialName", materialName.trim());
    formData.append("brand", brand.trim());
    formData.append("productCode", productCode.trim());
    formData.append("materialType", matType);
    formData.append("zones", selectedArea);
    formData.append("areas", selectedArea);
    // formData.append("sections", "Walls");
    // formData.append("maintenance", "N/A");
    // formData.append("minCost", "0");
    // formData.append("maxCost", "0");
    // formData.append("leadTime", "0");
    // formData.append("vendorAvailable", "false");

    // Format otherDetails as an object
    const detailsObj = {};
    otherDetails.forEach((item) => {
      if (item.key.trim() && item.value.trim()) {
        detailsObj[item.key.trim()] = item.value.trim();
      }
    });

    const extraFields = [
      { key: "mrpPrice", value: mrpPrice },
      //   { key: "color", value: color },
      { key: "material", value: material },
      { key: "size", value: size },
      { key: "category", value: category },
      { key: "subCategory", value: subCategory },
    ];

    extraFields.forEach(({ key, value }) => {
      if (value !== "" && value !== null && value !== undefined) {
        if (key === "mrpPrice") {
          detailsObj[key] = Number(value);
        } else {
          detailsObj[key] = typeof value === "string" ? value.trim() : value;
        }
      }
    });

    formData.append("otherDetails", JSON.stringify(detailsObj));

    // Append multiple files for images[]
    images.forEach((file) => {
      formData.append("images", file);
    });
    console.log("MAterial form data", formData);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACK_URL}/api/material/add`,
        formData,
        getAxiosConfig({}, true),
      );

      toast({
        title: "Material Added Successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (typeof onClose === "function") {
        onClose(true);
      }
    } catch (error) {
      console.error("Error adding material:", error);
      toast({
        title: "Submission failed",
        description: error?.response?.data?.message || "Something went wrong.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="200px"
      >
        <Spinner size="lg" color="teal.500" />
      </Box>
    );
  }

  return (
    <Box as="form" onSubmit={handleSubmit} className="space-y-6 pt-2">
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
        {/* Left Side fields */}
        <GridItem className="space-y-4">
          <FormControl isRequired>
            <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
              Material Name
            </FormLabel>
            <Input
              placeholder="e.g. acian paints blue2"
              value={materialName}
              onChange={(e) => {
                setMaterialName(e.target.value);
              }}
              focusBorderColor="teal.500"
              borderRadius="md"
            />
          </FormControl>

          <Grid templateColumns="1fr 1fr" gap={4}>
            <FormControl>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
                Brand
              </FormLabel>
              <Input
                placeholder="e.g. Asian Paints"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                focusBorderColor="teal.500"
                borderRadius="md"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
                Product Code
              </FormLabel>
              <Input
                placeholder="e.g. AP-102"
                value={productCode}
                onChange={(e) => setProductCode(e.target.value)}
                focusBorderColor="teal.500"
                borderRadius="md"
              />
            </FormControl>
          </Grid>

          <FormControl>
            <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
              MRP Price
            </FormLabel>
            <Input
              //   type="number"
              placeholder="e.g. 1500"
              value={mrpPrice}
              onChange={(e) => {
                if (e.target.value == "" || !isNaN(e.target.value)) {
                  setMrpPrice(
                    e.target.value == "" ? "" : Number(e.target.value),
                  );
                }
              }}
              focusBorderColor="teal.500"
              borderRadius="md"
            />
          </FormControl>

          <Grid templateColumns="1fr 1fr" gap={4}>
            {/* <FormControl>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
                Color
              </FormLabel>
              <Input
                placeholder="e.g. Blue"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                focusBorderColor="teal.500"
                borderRadius="md"
              />
            </FormControl> */}

            <FormControl>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
                Material
              </FormLabel>
              <Input
                placeholder="e.g. Wood"
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                focusBorderColor="teal.500"
                borderRadius="md"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
                Size
              </FormLabel>
              <Input
                placeholder="e.g. 1200x600 mm"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                focusBorderColor="teal.500"
                borderRadius="md"
              />
            </FormControl>
          </Grid>

          <Grid templateColumns="1fr 1fr" gap={4}>
            <FormControl>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
                Category
              </FormLabel>
              <Input
                placeholder="e.g. Flooring"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                focusBorderColor="teal.500"
                borderRadius="md"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
                Sub-Category
              </FormLabel>
              <Input
                placeholder="e.g. Engineered Wood"
                value={subCategory}
                onChange={(e) => setSubCategory(e.target.value)}
                focusBorderColor="teal.500"
                borderRadius="md"
              />
            </FormControl>
          </Grid>

          <FormControl isRequired>
            <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
              Area
            </FormLabel>
            <Select
              placeholder="Select Area"
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              focusBorderColor="teal.500"
              borderRadius="md"
            >
              {areasOptions.map((area) => (
                <option key={area} value={area}>
                  {area}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
              Material Type
            </FormLabel>
            <HStack>
              {!isCustomType ? (
                <Select
                  placeholder="Select Material Type"
                  value={selectedMaterialType}
                  onChange={(e) => {
                    if (e.target.value === "CUSTOM") {
                      setIsCustomType(true);
                      setSelectedMaterialType("");
                    } else {
                      setSelectedMaterialType(e.target.value);
                    }
                  }}
                  focusBorderColor="teal.500"
                  borderRadius="md"
                  flex="1"
                >
                  {materialTypeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                  <option
                    value="CUSTOM"
                    style={{ fontWeight: "bold", color: "#319795" }}
                  >
                    + Add Custom Value
                  </option>
                </Select>
              ) : (
                <HStack flex="1">
                  <Input
                    placeholder="Enter custom material type"
                    value={customMaterialType}
                    onChange={(e) => setCustomMaterialType(e.target.value)}
                    focusBorderColor="teal.500"
                    borderRadius="md"
                  />
                  <IconButton
                    icon={<Icon icon="fluent:arrow-undo-20-filled" />}
                    onClick={() => {
                      setIsCustomType(false);
                      setCustomMaterialType("");
                    }}
                    aria-label="Back to selection"
                    variant="ghost"
                    colorScheme="teal"
                  />
                </HStack>
              )}
            </HStack>
          </FormControl>
        </GridItem>

        {/* Right Side Upload & Dynamic Fields */}
        <GridItem className="space-y-5">
          {/* Images Upload */}
          <FormControl isRequired>
            <FormLabel fontWeight="medium" fontSize="sm" color="gray.700">
              Upload Images
            </FormLabel>
            <Box
              border="2px dashed"
              borderColor="gray.200"
              borderRadius="lg"
              p={4}
              textAlign="center"
              bg="gray.50"
              _hover={{ borderColor: "teal.400", bg: "teal.50/10" }}
              cursor="pointer"
              position="relative"
              transition="all 0.2s"
            >
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageChange}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  cursor: "pointer",
                }}
              />
              <VStack spacing={1}>
                <Box p={2} bg="teal.50" borderRadius="full" color="teal.500">
                  <FiUpload size={20} />
                </Box>
                <Text fontSize="sm" fontWeight="semibold" color="teal.600">
                  Click to upload images
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Only image files are allowed
                </Text>
              </VStack>
            </Box>

            {/* Previews */}
            {imagePreviews.length > 0 && (
              <HStack
                spacing={2}
                overflowX="auto"
                py={3}
                px={1}
                className="scrollbar-thin"
              >
                {imagePreviews.map((url, idx) => (
                  <Box
                    key={idx}
                    position="relative"
                    w="70px"
                    h="70px"
                    borderRadius="md"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="gray.200"
                    flexShrink={0}
                  >
                    <img
                      src={url}
                      alt={`preview-${idx}`}
                      className="w-full h-full object-cover"
                    />
                    <IconButton
                      icon={<FiTrash2 size={12} />}
                      size="xs"
                      colorScheme="red"
                      position="absolute"
                      top="2px"
                      right="2px"
                      onClick={() => removeImage(idx)}
                      aria-label="Remove image"
                      borderRadius="full"
                    />
                  </Box>
                ))}
              </HStack>
            )}
          </FormControl>

          {/* Dynamic otherDetails Keys/Values */}
          <Box className="space-y-3">
            <HStack justify="space-between">
              <Text fontWeight="medium" fontSize="sm" color="gray.700">
                Specifications / Other Details
              </Text>
              <Button
                leftIcon={<FiPlus />}
                size="xs"
                variant="ghost"
                colorScheme="teal"
                onClick={addDetailRow}
              >
                Add Row
              </Button>
            </HStack>

            <VStack
              spacing={2}
              align="stretch"
              maxH="200px"
              overflowY="auto"
              className="pr-1"
            >
              {otherDetails.map((item, idx) => (
                <HStack key={idx} spacing={2}>
                  <Input
                    placeholder="Field Name (Key)"
                    value={item.key}
                    onChange={(e) =>
                      handleDetailChange(idx, "key", e.target.value)
                    }
                    size="sm"
                    borderRadius="md"
                  />
                  <Input
                    placeholder="Value"
                    value={item.value}
                    onChange={(e) =>
                      handleDetailChange(idx, "value", e.target.value)
                    }
                    size="sm"
                    borderRadius="md"
                  />
                  <IconButton
                    icon={<FiTrash2 size={14} />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => removeDetailRow(idx)}
                    aria-label="Remove row"
                  />
                </HStack>
              ))}
            </VStack>
          </Box>
        </GridItem>
      </Grid>

      {/* Action buttons */}
      <HStack
        justify="flex-end"
        spacing={3}
        pt={4}
        borderTop="1px solid"
        borderColor="gray.100"
      >
        <Button
          onClick={(e) => onClose(false)}
          variant="outline"
          size="sm"
          borderRadius="md"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          colorScheme="teal"
          isLoading={loading}
          loadingText="Adding..."
          size="sm"
          borderRadius="md"
        >
          Add Material
        </Button>
      </HStack>
    </Box>
  );
};

export default AddMaterialButton;
